// Step 7 Phase 3 M2 — Full SparkViewerAdapter (non-XR).
//
// Scope M2 (per STEP7_F4_PHASE3_PLAN.md §M2):
//   - init + render loop with setAnimationLoop
//   - loadSplatScene with PendingLoad + idempotent disposePending + timeout + race-guard (§3.8)
//   - removeSplatScene
//   - raycast via SplatMesh.raycast (§3.4)
//   - captureFrame RAF-tick + canvas.toBlob, no preserveDrawingBuffer (§3.5)
//     XR-mode contract present (rejects on xrSession/pendingXRRequest/xrTransitioning)
//     even though M2 does not implement XR — M3 enterXR will activate it.
//   - computeFitCamera via SplatMesh.getBoundingBox (§3.4)
//   - setBeforeRender + onFrame frameListeners (§3.6)
//   - context loss handlers (webglcontextlost/webglcontextrestored)
//   - dispose with proper ordering (§3.7)
//
// XR (enterXR/exitXR) lands in M3.
//
// fileType mapping (Codex iter 1 §B2):
//   SplatFileType enum is `PLY|SPZ|SPLAT|KSPLAT|PCSOGS|PCSOGSZIP|RAD`.
//   Explicit SPLAT/KSPLAT for legacy formats; undefined for ply/
//   compressed.ply/sog (auto-detect via magic bytes by Spark SplatLoader).

import { SparkRenderer, SplatFileType, SplatMesh } from '@sparkjsdev/spark';
import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { VignetteShader } from 'three/addons/shaders/VignetteShader.js';
import type { ModelBbox, ModelFormat, Vec3 } from '../api/types.js';
import { AR_SESSION_INIT, VR_SESSION_INIT } from '../xr/types.js';
import type { CameraFit, CapturedFrame, ViewerAdapter } from './adapter.js';
import { fitCameraFromBox3, overviewCameraFromBox3 } from './auto-fit.js';
import {
  ColorGradeShader,
  DEFAULT_POST_FX_OPTIONS,
  normalizePostFxOptions,
  type PostFxOptions,
  shouldRenderPostFx,
} from './post-fx.js';
import type {
  CameraState,
  EngineId,
  InitOptions,
  LoadOptions,
  Projected,
  RaycastHit,
  ViewerCapabilities,
  ViewerEventDetailMap,
  ViewportSize,
} from './types.js';

// M3 capabilities — full Spark adapter incl. WebXR base capability.
// WebXR scope ridotto B-PURO: capability declared, NOT commercial-grade
// VR/AR (utente conferma: VR commerciale = build Unity separata).
const M3_CAPABILITIES: ViewerCapabilities = Object.freeze({
  dispose: true,
  removeSplatScene: true,
  raycast: true,
  enterXR: true,
  exitXR: true,
  onProgress: true,
  contextLossHandler: true,
  beforeRenderHook: true,
  frameCallbacks: true,
  captureFrame: true,
  cameraFit: true,
  postFx: true,
  // FASE 1 PR2 — adapter espone API engine-neutral. Fake adapter nei test
  // (orchestrator.test.ts:55 + tour-animator.test.ts:31) NON cambiano: capability
  // OPZIONALE (types.ts), assenza = false.
  engineNeutralCamera: true,
});

const DEFAULT_LOAD_TIMEOUT_MS = 60_000;

interface PendingLoad {
  seq: number;
  mesh: SplatMesh;
  timeoutId: ReturnType<typeof setTimeout> | null;
  // Codex iter 4 NEW1 fix — idempotency flag. All paths route via
  // disposePending(); helper checks `disposed` so repeated calls are no-op.
  disposed: boolean;
}

interface CaptureRequest {
  resolve: (frame: CapturedFrame) => void;
  reject: (err: Error) => void;
}

interface ContextLossHandler {
  event: string;
  handler: EventListener;
}

function mapFormatToSparkFileType(format?: ModelFormat): SplatFileType | undefined {
  if (format === undefined) return undefined;
  switch (format) {
    case 'splat':
      return SplatFileType.SPLAT;
    case 'ksplat':
      return SplatFileType.KSPLAT;
    case 'rad':
      // WS4: LOD-tree streamabile (paged). fileType esplicito.
      return SplatFileType.RAD;
    case 'ply':
    case 'compressed.ply':
    case 'sog':
      // Auto-detect via magic bytes — Spark internal SplatLoader.
      return undefined;
  }
}

export interface SparkAdapterOptions {
  loadTimeoutMs?: number;
}

export class SparkViewerAdapter implements ViewerAdapter {
  readonly capabilities = M3_CAPABILITIES;
  readonly events = new EventTarget();
  // FASE 1 PR2 — engine identifier per type guard canUseEngineNeutralCamera().
  readonly engine: EngineId = 'spark';

  private renderer: THREE.WebGLRenderer | null = null;
  private spark: SparkRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private splatMesh: SplatMesh | null = null;

  // FASE 1 PR2 — cache di lookAt esplicito (target). Three.PerspectiveCamera
  // espone solo quaternion: il target NON e' derivabile senza ambiguita' dalla
  // sola orientation (Codex INDIPENDENT blind-spot). Aggiornato da setCamera +
  // setCameraState; restituito da getCameraState come `lookAt`. Default
  // [0,0,-1] perche' camera default guarda -Z (Three convention).
  // Best-effort: getCamera() / OrbitControls.pan possono rendere stale il
  // cache — sara' risolto in PR3 quando l'adapter ownerizza OrbitControls.
  private lastLookAt: Vec3 = [0, 0, -1];
  private lastLookAtDistance = 1;
  // WS4: bbox della mesh corrente (dal manifest), usato per inquadrare la camera
  // quando la mesh e' `paged` (.rad) e getBoundingBox() di Spark lancia.
  private loadedBbox: ModelBbox | null = null;
  private rafId: number | null = null;
  private beforeRenderCb: (() => void) | null = null;
  private frameListeners = new Set<(now: number) => void>();
  private captureRequest: CaptureRequest | null = null;
  private contextLossHandlers: ContextLossHandler[] = [];

  // Inc D — post-FX composer (lazy). null until the first setPostFx(true). Kept
  // alive once built; bypassed (renderer.render) when post-FX is off or in any
  // XR state. Refs to passes are held for resize/uniform-update/dispose.
  private composer: EffectComposer | null = null;
  private renderPass: RenderPass | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private colorGradePass: ShaderPass | null = null;
  private vignettePass: ShaderPass | null = null;
  private outputPass: OutputPass | null = null;
  private postFxRequested = false;
  private postFxOptions: PostFxOptions = DEFAULT_POST_FX_OPTIONS;

  // XR fields — declared per plan §3.1 (M3 will populate via enterXR/exitXR).
  // captureFrame guard already checks them so M3 swap doesn't need to retro-fit.
  private xrSession: XRSession | null = null;
  private pendingXRRequest: AbortController | null = null;
  private xrTransitioning = false;

  // Async load lifecycle (plan §3.8)
  private currentLoadSeq = 0;
  private pendingLoad: PendingLoad | null = null;
  private disposed = false;
  private readonly loadTimeoutMs: number;

  constructor(opts: SparkAdapterOptions = {}) {
    this.loadTimeoutMs = opts.loadTimeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS;
  }

  init = async (opts: InitOptions): Promise<void> => {
    const renderer = new THREE.WebGLRenderer({
      canvas: opts.canvas,
      antialias: false, // Spark docs: AA doesn't help splats + costs perf
    });
    const cappedDpr =
      opts.ignoreDevicePixelRatio === true
        ? 1
        : Math.min(window.devicePixelRatio, opts.maxPixelRatio ?? Number.POSITIVE_INFINITY);
    renderer.setPixelRatio(cappedDpr);
    renderer.setSize(opts.canvas.clientWidth, opts.canvas.clientHeight, false);

    // Inc D [CRITICAL A]: pin the color/alpha contract explicitly. These ARE the
    // Three 0.180 defaults (SRGBColorSpace + NoToneMapping + exposure 1), so it
    // is a no-op on the existing direct render path — but it makes the contract
    // OutputPass relies on explicit + future-proof. All grading happens INSIDE
    // the composer (ColorGradeShader), never via the shared renderer.
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.setClearAlpha(1);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      opts.canvas.clientWidth / Math.max(1, opts.canvas.clientHeight),
      0.01,
      1000,
    );

    // Plan §3 / Codex iter 1 Q9: WebGLRenderer FIRST, then SparkRenderer,
    // then scene.add(spark) BEFORE adding SplatMesh objects.
    // WS4: lodSplatCount = budget splat ATTIVI per-device (golden standard), settato
    // alla COSTRUZIONE (per-device, non per-variante). Innocuo per i flat legacy: Spark
    // applica il LOD solo a mesh paged/lodSplats (spark.module.js:10452). undefined =
    // default Spark per-piattaforma.
    const sparkOptions: ConstructorParameters<typeof SparkRenderer>[0] = { renderer };
    if (opts.lodSplatCount !== undefined) sparkOptions.lodSplatCount = opts.lodSplatCount;
    const spark = new SparkRenderer(sparkOptions);
    scene.add(spark);

    this.renderer = renderer;
    this.spark = spark;
    this.scene = scene;
    this.camera = camera;

    this.attachContextLossHandlers(opts.canvas);
    this.startWindowRAF();
  };

  loadSplatScene = async (url: string, opts: LoadOptions = {}): Promise<void> => {
    if (this.scene === null || this.camera === null) {
      throw new Error('SparkViewerAdapter.loadSplatScene called before init()');
    }

    // Plan §3.8 + Codex iter 3 NEW2 + iter 4 NEW1: dispose IMMEDIATA della
    // mesh pending precedente via helper idempotente.
    if (this.pendingLoad !== null) {
      this.disposePending(this.pendingLoad);
      this.pendingLoad = null;
    }

    // Dispose splatMesh assegnata precedente (replace scene)
    if (this.splatMesh !== null) {
      this.scene.remove(this.splatMesh);
      this.splatMesh.dispose();
      this.splatMesh = null;
      this.loadedBbox = null;
    }

    const seq = ++this.currentLoadSeq;
    const fileType = mapFormatToSparkFileType(opts.format);
    const meshOptions: ConstructorParameters<typeof SplatMesh>[0] = {
      url,
      raycastable: true,
      onProgress: (event: ProgressEvent) => {
        if (event.lengthComputable) {
          this.events.dispatchEvent(
            new CustomEvent<ViewerEventDetailMap['progress']>('progress', {
              detail: { loaded: event.loaded, total: event.total },
            }),
          );
        }
      },
    };
    if (fileType !== undefined) meshOptions.fileType = fileType;
    // WS4: la variante `rad` e' un LOD-tree streamabile → paged (no buffer in RAM).
    if (opts.format === 'rad') meshOptions.paged = true;
    const mesh = new SplatMesh(meshOptions);

    const pending: PendingLoad = { seq, mesh, timeoutId: null, disposed: false };
    this.pendingLoad = pending;

    const initWithTimeout = Promise.race([
      mesh.initialized,
      new Promise<never>((_, reject) => {
        pending.timeoutId = setTimeout(
          () => reject(new Error(`SplatMesh.initialized timeout after ${this.loadTimeoutMs}ms`)),
          this.loadTimeoutMs,
        );
      }),
    ]);

    try {
      await initWithTimeout;
    } catch (err) {
      this.disposePending(pending);
      if (this.pendingLoad === pending) this.pendingLoad = null;
      throw err;
    }

    // Race-guard: durante await arrivato altro load() / dispose() / abandoned.
    if (seq !== this.currentLoadSeq || this.disposed || this.pendingLoad !== pending) {
      this.disposePending(pending);
      return;
    }

    // Success: clear timer, attach mesh.
    if (pending.timeoutId !== null) {
      clearTimeout(pending.timeoutId);
      pending.timeoutId = null;
    }
    this.pendingLoad = null;
    this.scene.add(mesh);
    this.splatMesh = mesh;
    // WS4: bbox della mesh appena caricata (null per i flat → framing via getBoundingBox).
    this.loadedBbox = opts.bbox ?? null;
    this.events.dispatchEvent(
      new CustomEvent<ViewerEventDetailMap['loaded']>('loaded', { detail: { url } }),
    );
  };

  removeSplatScene = async (_sceneIndex: number): Promise<void> => {
    // Spark non ha multi-scene API come mkkellogg (un SplatMesh per adapter).
    // sceneIndex ignorato per backward compat con orchestrator chiamante.
    if (this.splatMesh === null || this.scene === null) return;
    this.scene.remove(this.splatMesh);
    this.splatMesh.dispose();
    this.splatMesh = null;
    this.loadedBbox = null;
  };

  raycast = (screenX: number, screenY: number): THREE.Vector3 | null => {
    if (this.camera === null || this.splatMesh === null || this.renderer === null) return null;
    const canvas = this.renderer.domElement;
    const ndc = new THREE.Vector2(
      (screenX / canvas.clientWidth) * 2 - 1,
      -(screenY / canvas.clientHeight) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    const hits: THREE.Intersection[] = [];
    this.splatMesh.raycast(raycaster, hits);
    if (hits.length === 0) return null;
    const hit = hits[0];
    return hit ? hit.point.clone() : null;
  };

  captureFrame = (): Promise<CapturedFrame> => {
    // Plan §3.5 + Codex iter 4 NEW2 + iter 5 NEW1: rifiuta durante xrSession,
    // pendingXRRequest in-flight, xrTransitioning rollback. M2 sempre permette
    // (XR fields stay null/false); M3 enterXR attiva il guard.
    if (this.xrSession !== null || this.pendingXRRequest !== null || this.xrTransitioning) {
      return Promise.reject(
        new Error(
          'captureFrame not supported during XR session (active, starting, or rolling back)',
        ),
      );
    }
    return new Promise((resolve, reject) => {
      this.captureRequest = { resolve, reject };
    });
  };

  // Plan §3.4 + Codex iter 1 Q8: Spark SplatMesh.getBoundingBox preferito a
  // THREE.Box3.setFromObject (splats non sono Three geometry standard).
  // Inc B: estratto per condividere il try/catch tra fit + overview.
  //
  // WS4 + Barnes fix 2026-05-31: il bbox del manifest HA PRECEDENZA sul bbox
  // calcolato da Spark dalla mesh. Motivo: per render interni (Barnes), gli
  // EXR includono lo sky visibile dalle finestre → la nuvola contiene splat
  // outlier molto distanti dall'interior → Spark.getBoundingBox restituisce
  // un volume enorme (es. 186m × 91m × 96m per un appartamento di 8m × 3m).
  // Il manifest bbox e' calcolato server-side col filtro percentile 5-95
  // dei centri splat → esclude outlier → inquadratura coerente con la scena.
  // Fallback su Spark bbox per modelli legacy senza manifest bbox.
  private getSplatBoundingBox(): THREE.Box3 | null {
    if (this.splatMesh === null) return null;
    const manifestBox = this.boxFromManifestBbox();
    if (manifestBox !== null) return manifestBox;
    let box: THREE.Box3 | null = null;
    try {
      box = this.splatMesh.getBoundingBox();
    } catch {
      // paged (.rad): getBoundingBox() lancia ("requires PackedSplats").
      box = null;
    }
    if (box !== null && !box.isEmpty()) return box;
    return null;
  }

  // WS4: costruisce un THREE.Box3 dal bbox del manifest (mesh paged .rad).
  private boxFromManifestBbox(): THREE.Box3 | null {
    if (this.loadedBbox === null) return null;
    const { min, max } = this.loadedBbox;
    return new THREE.Box3(
      new THREE.Vector3(min[0], min[1], min[2]),
      new THREE.Vector3(max[0], max[1], max[2]),
    );
  }

  computeFitCamera = (): CameraFit | null => {
    if (this.camera === null) return null;
    const box = this.getSplatBoundingBox();
    if (box === null) return null;
    return fitCameraFromBox3(box, this.camera.fov);
  };

  // Inc B: dollhouse/overview camera dallo stesso bbox splat.
  computeOverviewCamera = (): CameraFit | null => {
    if (this.camera === null) return null;
    const box = this.getSplatBoundingBox();
    if (box === null) return null;
    return overviewCameraFromBox3(box, this.camera.fov);
  };

  // Inc D — enable/disable post-FX. Lazily builds the EffectComposer on first
  // enable (default OFF → zero RT cost until used). renderTick bypasses it while
  // in any XR state. `opts` (if provided) is normalized + applied to the passes.
  setPostFx = (enabled: boolean, opts?: PostFxOptions): void => {
    if (this.disposed) return;
    if (opts !== undefined) this.postFxOptions = normalizePostFxOptions(opts);
    if (enabled && this.composer === null) {
      this.buildComposer();
    } else if (this.composer !== null && opts !== undefined) {
      this.applyPostFxOptions(this.postFxOptions);
    }
    this.postFxRequested = enabled;
  };

  // Inc D — keep composer RTs in sync with the canvas. Called by the
  // orchestrator's single ResizeObserver right after renderer.setSize(w,h,false).
  resizePostFx = (width: number, height: number, pixelRatio?: number): void => {
    if (this.composer === null || this.renderer === null) return;
    this.composer.setPixelRatio(pixelRatio ?? this.renderer.getPixelRatio());
    this.composer.setSize(width, height);
  };

  private buildComposer(): void {
    if (this.renderer === null || this.scene === null || this.camera === null) return;
    const renderer = this.renderer;
    const size = renderer.getSize(new THREE.Vector2());
    const composer = new EffectComposer(renderer);
    composer.setPixelRatio(renderer.getPixelRatio());
    composer.setSize(size.x, size.y);

    // Order (Codex [WARNING] B): scene → bloom (pre-tonemap) → grade → vignette
    // → output (linear→sRGB). ACES lives in ColorGradeShader, NOT in
    // renderer.toneMapping — the shared renderer must stay neutral ([CRITICAL] A).
    const renderPass = new RenderPass(this.scene, this.camera);
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(size.x, size.y),
      this.postFxOptions.bloom.strength,
      this.postFxOptions.bloom.radius,
      this.postFxOptions.bloom.threshold,
    );
    const colorGradePass = new ShaderPass(ColorGradeShader);
    const vignettePass = new ShaderPass(VignetteShader);
    const outputPass = new OutputPass();
    composer.addPass(renderPass);
    composer.addPass(bloomPass);
    composer.addPass(colorGradePass);
    composer.addPass(vignettePass);
    composer.addPass(outputPass);

    this.composer = composer;
    this.renderPass = renderPass;
    this.bloomPass = bloomPass;
    this.colorGradePass = colorGradePass;
    this.vignettePass = vignettePass;
    this.outputPass = outputPass;
    this.applyPostFxOptions(this.postFxOptions);
  }

  private applyPostFxOptions(opts: PostFxOptions): void {
    if (this.bloomPass !== null) {
      this.bloomPass.strength = opts.bloom.strength;
      this.bloomPass.radius = opts.bloom.radius;
      this.bloomPass.threshold = opts.bloom.threshold;
    }
    const setU = (
      uniforms: { [k: string]: { value: unknown } },
      key: string,
      value: number,
    ): void => {
      const u = uniforms[key];
      if (u !== undefined) u.value = value;
    };
    if (this.colorGradePass !== null) {
      const u = this.colorGradePass.uniforms;
      setU(u, 'exposure', opts.colorGrade.exposure);
      setU(u, 'contrast', opts.colorGrade.contrast);
      setU(u, 'saturation', opts.colorGrade.saturation);
      setU(u, 'acesEnabled', opts.colorGrade.aces ? 1 : 0);
    }
    if (this.vignettePass !== null) {
      const u = this.vignettePass.uniforms;
      setU(u, 'offset', opts.vignette.offset);
      setU(u, 'darkness', opts.vignette.darkness);
    }
  }

  // Drop JS refs without touching GL (context-lost: GPU resources already
  // invalidated by the driver). Composer rebuilds lazily on restore/next enable.
  private clearPostFxRefs(): void {
    this.composer = null;
    this.renderPass = null;
    this.bloomPass = null;
    this.colorGradePass = null;
    this.vignettePass = null;
    this.outputPass = null;
  }

  // Full GL teardown (adapter dispose). EffectComposer.dispose() frees only its
  // own RTs + copyPass, so each added pass is disposed explicitly.
  private disposePostFx(): void {
    if (this.composer !== null) {
      this.renderPass?.dispose();
      this.bloomPass?.dispose();
      this.colorGradePass?.dispose();
      this.vignettePass?.dispose();
      this.outputPass?.dispose();
      this.composer.dispose();
    }
    this.clearPostFxRefs();
  }

  setBeforeRender = (cb: (() => void) | null): void => {
    this.beforeRenderCb = cb;
  };

  onFrame = (cb: (now: number) => void): (() => void) => {
    this.frameListeners.add(cb);
    return () => {
      this.frameListeners.delete(cb);
    };
  };

  // Plan §3.3 + Codex iter 2 §B6 + iter 3 NEW1 + iter 4 NEW2 + iter 5 NEW1:
  // transactional WebXR lifecycle. Scope ridotto B-PURO: minimal lifecycle
  // correctness (capability-only, NOT commercial-grade Quest/Vision Pro).
  enterXR = async (mode: 'vr' | 'ar'): Promise<void> => {
    if (this.xrSession !== null) return; // idempotent
    if (this.renderer === null || this.disposed) {
      throw new Error('SparkViewerAdapter not initialized');
    }
    if (typeof navigator === 'undefined' || navigator.xr === undefined) {
      throw new Error('WebXR not available in this browser');
    }
    // Codex iter 4 NEW2: cancel pending captureRequest PRIMA di setAnimationLoop
    // swap. Evita fulfill in XR mode (blob blank).
    this.rejectPendingCapture('captureFrame canceled: XR session starting');

    this.pendingXRRequest = new AbortController();
    const sessionMode: XRSessionMode = mode === 'vr' ? 'immersive-vr' : 'immersive-ar';
    const sessionInit = mode === 'vr' ? VR_SESSION_INIT : AR_SESSION_INIT;

    let session: XRSession;
    try {
      session = await navigator.xr.requestSession(sessionMode, sessionInit);
    } catch (err) {
      this.pendingXRRequest = null;
      throw err;
    }
    if (this.disposed || this.pendingXRRequest === null) {
      // Disposed o canceled durante requestSession — chiudere immediatamente.
      void session.end().catch(() => {});
      return;
    }
    this.pendingXRRequest = null;

    // Pre-assign listener + xrSession PRIMA di setSession (Codex iter 2 §B6).
    session.addEventListener('end', this.handleSessionEnd);
    this.xrSession = session;

    // ORDER MATTERS (Codex iter 3 NEW1, allineato a mkkellogg-adapter.ts:448-459):
    // Cancel window RAF, enable xr, setAnimationLoop FIRST, THEN setSession.
    // Reverse order would resume window RAF in parallel with XR RAF.
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.renderer.xr.enabled = true;
    this.renderer.setAnimationLoop(this.renderTick);

    try {
      await this.renderer.xr.setSession(session);
    } catch (err) {
      // Codex iter 5 NEW1: xrTransitioning=true chiude finestra captureFrame
      // race durante rollback (xrSession=null + pendingXRRequest=null ma
      // renderer ancora in stato XR-transitioning).
      this.xrTransitioning = true;
      try {
        // Transactional rollback: detach listener + null xrSession FIRST,
        // session.end (lets WebXRManager run cleanup), setAnimationLoop(null) +
        // enabled=false (Three.js reacts to sessionend; disable AFTER), restart
        // standard RAF.
        session.removeEventListener('end', this.handleSessionEnd);
        this.xrSession = null;
        try {
          await session.end();
        } catch {
          /* best-effort */
        }
        try {
          this.renderer.setAnimationLoop(null);
        } catch {
          /* ignore */
        }
        this.renderer.xr.enabled = false;
        if (!this.disposed) this.startWindowRAF();
      } finally {
        this.xrTransitioning = false;
      }
      throw err;
    }

    this.events.dispatchEvent(
      new CustomEvent<ViewerEventDetailMap['xr-session-started']>('xr-session-started', {
        detail: { mode },
      }),
    );
  };

  exitXR = async (): Promise<void> => {
    if (this.xrSession === null) return;
    try {
      await this.xrSession.end();
    } catch {
      /* idempotent */
    }
  };

  private handleSessionEnd = (): void => {
    if (this.xrSession !== null) {
      this.xrSession.removeEventListener('end', this.handleSessionEnd);
      this.xrSession = null;
    }
    if (this.renderer !== null) {
      try {
        this.renderer.setAnimationLoop(null);
      } catch {
        /* ignore */
      }
      this.renderer.xr.enabled = false;
    }
    if (!this.disposed) this.startWindowRAF();
    this.events.dispatchEvent(
      new CustomEvent<ViewerEventDetailMap['xr-session-ended']>('xr-session-ended', {
        detail: {},
      }),
    );
  };

  private rejectPendingCapture(reason: string): void {
    if (this.captureRequest === null) return;
    const req = this.captureRequest;
    this.captureRequest = null;
    req.reject(new Error(reason));
  }

  getThreeScene = (): THREE.Scene => {
    if (this.scene === null) throw new Error('SparkViewerAdapter not initialized');
    return this.scene;
  };

  getCamera = (): THREE.PerspectiveCamera => {
    if (this.camera === null) throw new Error('SparkViewerAdapter not initialized');
    return this.camera;
  };

  getRenderer = (): THREE.WebGLRenderer => {
    if (this.renderer === null) throw new Error('SparkViewerAdapter not initialized');
    return this.renderer;
  };

  setCamera = (
    pos: readonly [number, number, number],
    lookAt: readonly [number, number, number],
    fov?: number,
  ): void => {
    if (this.camera === null) throw new Error('SparkViewerAdapter not initialized');
    this.camera.position.set(pos[0], pos[1], pos[2]);
    this.camera.lookAt(lookAt[0], lookAt[1], lookAt[2]);
    if (fov !== undefined) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    // FASE 1 PR2 — cache target esplicito per getCameraState() engine-neutral.
    this.lastLookAt = [lookAt[0], lookAt[1], lookAt[2]];
    const dx = lookAt[0] - pos[0];
    const dy = lookAt[1] - pos[1];
    const dz = lookAt[2] - pos[2];
    const d = Math.hypot(dx, dy, dz);
    this.lastLookAtDistance = d > 0 ? d : this.lastLookAtDistance;
  };

  // FASE 1 PR2 — engine-neutral API (Codex INDIPENDENT review 6/7 allineato).
  // Pattern: additivi su ViewerAdapter, mantengono setCamera/getCamera legacy
  // per 9 call site orchestrator + 2 call site tour (rimozione = PR3).

  getCameraState = (): CameraState => {
    if (this.camera === null) throw new Error('SparkViewerAdapter not initialized');
    const cam = this.camera;
    const pos: Vec3 = [cam.position.x, cam.position.y, cam.position.z];
    const q = cam.quaternion;
    return {
      pos,
      // Cache: best-effort. Stale se camera viene mutata via getCamera()
      // o OrbitControls.pan dall'orchestrator (sara' risolto PR3).
      lookAt: this.lastLookAt,
      fov: cam.fov,
      quaternion: [q.x, q.y, q.z, q.w],
    };
  };

  setCameraState = (state: CameraState): void => {
    if (this.camera === null) throw new Error('SparkViewerAdapter not initialized');
    const cam = this.camera;
    cam.position.set(state.pos[0], state.pos[1], state.pos[2]);
    // Codex INDIPENDENT: lookAt HA PRECEDENZA su quaternion. Contratto storico
    // setCamera(pos, lookAt, fov) dominante (9 call site orchestrator + 2 tour);
    // share URL / preset / tour dipendono dal target esplicito. Quaternion
    // solo fallback se lookAt degenere (pos == lookAt).
    const lookAtDegenerate =
      state.lookAt[0] === state.pos[0] &&
      state.lookAt[1] === state.pos[1] &&
      state.lookAt[2] === state.pos[2];
    if (!lookAtDegenerate) {
      cam.lookAt(state.lookAt[0], state.lookAt[1], state.lookAt[2]);
      this.lastLookAt = [state.lookAt[0], state.lookAt[1], state.lookAt[2]];
      const dx = state.lookAt[0] - state.pos[0];
      const dy = state.lookAt[1] - state.pos[1];
      const dz = state.lookAt[2] - state.pos[2];
      this.lastLookAtDistance = Math.hypot(dx, dy, dz);
    } else if (state.quaternion !== undefined) {
      const q = state.quaternion;
      cam.quaternion.set(q[0], q[1], q[2], q[3]);
      // Aggiorna cache: forward * lastDistance (best-effort).
      const fwd = cam.getWorldDirection(new THREE.Vector3());
      this.lastLookAt = [
        cam.position.x + fwd.x * this.lastLookAtDistance,
        cam.position.y + fwd.y * this.lastLookAtDistance,
        cam.position.z + fwd.z * this.lastLookAtDistance,
      ];
    }
    if (state.fov !== cam.fov) {
      cam.fov = state.fov;
    }
    // Codex INDIPENDENT: matrici aggiornate ESPLICITAMENTE per evitare projection
    // stale se chiami projectWorldToScreen() immediatamente dopo setCameraState.
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
  };

  getViewport = (): ViewportSize => {
    if (this.renderer === null) throw new Error('SparkViewerAdapter not initialized');
    const canvas = this.renderer.domElement;
    return {
      width: canvas.clientWidth,
      height: canvas.clientHeight,
      pixelRatio: this.renderer.getPixelRatio(),
    };
  };

  // Codex INDIPENDENT: viewport OPZIONALE (default = getViewport()), meno
  // verbose nei call site. Stessa matematica di hotspots/projection.ts:15-31
  // (PR4 unifichera' i due quando hotspot adotta adapter.projectWorldToScreen).
  projectWorldToScreen = (world: Vec3, viewport?: ViewportSize): Projected => {
    if (this.camera === null) throw new Error('SparkViewerAdapter not initialized');
    const vp = viewport ?? this.getViewport();
    const v = new THREE.Vector3(world[0], world[1], world[2]);
    const distance = v.distanceTo(this.camera.position);
    v.project(this.camera);
    const visible = v.z >= -1 && v.z <= 1 && v.x >= -1 && v.x <= 1 && v.y >= -1 && v.y <= 1;
    return {
      screenX: ((v.x + 1) / 2) * vp.width,
      screenY: ((1 - v.y) / 2) * vp.height,
      visible,
      distance,
    };
  };

  // Codex INDIPENDENT: wrapper su raycast() esistente che ritorna RaycastHit
  // (point + distance) invece di Vector3 puro. Stessa logica per non divergere
  // dal comportamento legacy (PR4 hotspot/measure adotteranno questo).
  raycastHit = (screenX: number, screenY: number): RaycastHit | null => {
    if (this.camera === null || this.splatMesh === null || this.renderer === null) return null;
    const canvas = this.renderer.domElement;
    const ndc = new THREE.Vector2(
      (screenX / canvas.clientWidth) * 2 - 1,
      -(screenY / canvas.clientHeight) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    const hits: THREE.Intersection[] = [];
    this.splatMesh.raycast(raycaster, hits);
    if (hits.length === 0) return null;
    const hit = hits[0];
    if (!hit) return null;
    return {
      point: [hit.point.x, hit.point.y, hit.point.z],
      distance: hit.distance,
    };
  };

  // FASE 1 PR2 — engine-neutral resize. PR3 sostituira' i call site orchestrator
  // a `getRenderer().setSize()` + `resizePostFx()` con un singolo `adapter.resize()`.
  resize = (size: ViewportSize): void => {
    if (this.renderer === null) return;
    this.renderer.setPixelRatio(size.pixelRatio);
    this.renderer.setSize(size.width, size.height, false);
    if (this.composer !== null) {
      this.composer.setPixelRatio(size.pixelRatio);
      this.composer.setSize(size.width, size.height);
    }
    if (this.camera !== null) {
      this.camera.aspect = size.width / Math.max(1, size.height);
      this.camera.updateProjectionMatrix();
    }
  };

  dispose = async (): Promise<void> => {
    // Plan §3.7 dispose ordering:
    //  1. mark disposed (race-guard for in-flight loadSplatScene + XR start)
    //  2. cancel pendingXRRequest if in-flight
    //  3. end active xrSession + reset renderer.xr state
    //  4. cancel RAF
    //  5. dispose pending load (idempotent helper)
    //  6. dispose splatMesh
    //  7. reject pending capture
    //  8. detach context loss listeners
    //  9. dispose Spark/Three resources
    this.disposed = true;
    // Cancel pending XR request (in-flight requestSession will end via
    // disposed-check after its await resolves).
    this.pendingXRRequest = null;
    // End active session if present (handleSessionEnd will fire and clean up).
    if (this.xrSession !== null) {
      try {
        await this.xrSession.end();
      } catch {
        /* best-effort */
      }
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.pendingLoad !== null) {
      this.disposePending(this.pendingLoad);
      this.pendingLoad = null;
    }
    if (this.splatMesh !== null && this.scene !== null) {
      this.scene.remove(this.splatMesh);
      this.splatMesh.dispose();
      this.splatMesh = null;
    }
    this.loadedBbox = null;
    // Reject any pending captureRequest so callers don't hang.
    this.rejectPendingCapture('captureFrame canceled: adapter disposed');
    this.detachContextLossHandlers();
    if (this.spark !== null && this.scene !== null) {
      this.scene.remove(this.spark);
    }
    this.beforeRenderCb = null;
    this.frameListeners.clear();
    // Inc D: dispose composer + passes BEFORE renderer.dispose().
    this.disposePostFx();
    if (this.renderer !== null) {
      this.renderer.dispose();
      this.renderer = null;
    }
    this.spark = null;
    this.scene = null;
    this.camera = null;
  };

  private disposePending(p: PendingLoad): void {
    // Codex iter 4 NEW1: idempotent — safe to call from any path
    // (second-load, catch reject, race-guard, dispose adapter, timeout fire).
    if (p.disposed) return;
    p.disposed = true;
    if (p.timeoutId !== null) {
      clearTimeout(p.timeoutId);
      p.timeoutId = null;
    }
    p.mesh.dispose();
  }

  private fulfillCaptureIfPending(): void {
    const req = this.captureRequest;
    if (req === null || this.renderer === null) return;
    this.captureRequest = null;
    this.renderer.domElement.toBlob((blob) => {
      if (blob === null) {
        req.reject(new Error('canvas.toBlob returned null'));
        return;
      }
      const url = URL.createObjectURL(blob);
      req.resolve({
        blob,
        revoke: () => URL.revokeObjectURL(url),
      });
    }, 'image/png');
  }

  private renderTick = (now: number): void => {
    if (this.renderer === null || this.scene === null || this.camera === null) return;
    if (this.beforeRenderCb !== null) this.beforeRenderCb();
    for (const cb of this.frameListeners) cb(now);
    // Inc D [CRITICAL F]: render through the composer only when post-FX is
    // requested AND we are fully outside any XR state (EffectComposer is
    // incompatible with WebXRManager's setAnimationLoop). Otherwise the direct
    // render path — byte-identical to pre-Inc-D.
    if (
      this.composer !== null &&
      shouldRenderPostFx({
        postFxRequested: this.postFxRequested,
        hasComposer: true,
        xrSessionActive: this.xrSession !== null,
        pendingXRRequest: this.pendingXRRequest !== null,
        xrTransitioning: this.xrTransitioning,
        isPresenting: this.renderer.xr.isPresenting === true,
      })
    ) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
    // Plan §3.5: capture in same RAF tick AFTER render() so canvas pixels
    // are fresh (no need for preserveDrawingBuffer always-on).
    this.fulfillCaptureIfPending();
  };

  private startWindowRAF(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    const tick = (now: number): void => {
      this.renderTick(now);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private attachContextLossHandlers(canvas: HTMLCanvasElement): void {
    const lostHandler = (ev: Event): void => {
      ev.preventDefault();
      // Inc D: GPU resources are invalidated by the driver — drop composer refs
      // (no GL calls). It rebuilds lazily on restore (below) or next enable.
      this.clearPostFxRefs();
      this.events.dispatchEvent(new CustomEvent('context-lost'));
    };
    const restoredHandler = (): void => {
      // Inc D: rebuild the composer if post-FX was active before the loss.
      if (this.postFxRequested && this.composer === null && !this.disposed) {
        this.buildComposer();
      }
      this.events.dispatchEvent(new CustomEvent('context-restored'));
    };
    canvas.addEventListener('webglcontextlost', lostHandler as EventListener, false);
    canvas.addEventListener('webglcontextrestored', restoredHandler as EventListener, false);
    this.contextLossHandlers.push(
      { event: 'webglcontextlost', handler: lostHandler as EventListener },
      { event: 'webglcontextrestored', handler: restoredHandler as EventListener },
    );
  }

  private detachContextLossHandlers(): void {
    if (this.renderer === null) return;
    const canvas = this.renderer.domElement;
    for (const { event, handler } of this.contextLossHandlers) {
      canvas.removeEventListener(event, handler);
    }
    this.contextLossHandlers = [];
  }
}
